import {
  SessionTaskAttemptResultEnum,
  SessionTaskAttemptStateEnum,
} from "~/lib/objects";
import type { SessionTaskAttempt, Problem } from "~/lib/objects";
import { calculateRatingDeltaFromLife } from "~/utils/ratingCalculations";

export enum StatsCategoryType {
  WIN = "WIN",
  DRAW = "DRAW",
  RETRY_GROUP = "RETRY_GROUP",
  EXTERNAL_HINT = "EXTERNAL_HINT",
  LOSS = "LOSS",
}

export interface DetailedStatisticsCategoryTask {
  problemId: number;
  name: string;
}

export interface DetailedStatisticsCategory {
  type: StatsCategoryType;
  retryCount: number;
  count: number;
  earnedPoints: number;
  maxPoints: number;
  tasks: DetailedStatisticsCategoryTask[];
  subCategories?: DetailedStatisticsCategory[];
}

export interface DetailedStatistics {
  totalCount: number;
  uncompletedCount: number;
  solvedCount: number;
  earnedPointsAll: number;
  maxPointsAll: number;
  earnedPointsSolved: number;
  maxPointsSolved: number;
  categories: DetailedStatisticsCategory[];
}

export function createEmptyDetailedStatistics(): DetailedStatistics {
  return {
    totalCount: 0,
    uncompletedCount: 0,
    solvedCount: 0,
    earnedPointsAll: 0,
    maxPointsAll: 0,
    earnedPointsSolved: 0,
    maxPointsSolved: 0,
    categories: [],
  };
}

type DetailedStatisticsOptions = {
  lifeRating?: number;
};

export function computeDetailedStatistics(
  attempts: SessionTaskAttempt[] = [],
  problems: Array<
    Partial<Problem> & {
      id?: number;
      problemId?: number;
      name?: string;
      title?: string;
      rating?: number | null;
    }
  > = [],
  options: DetailedStatisticsOptions = {},
): DetailedStatistics {
  const totalCount = Array.isArray(problems) ? problems.length : 0;
  const lifeRating =
    typeof options.lifeRating === "number" ? options.lifeRating : 0;

  const map = new Map<string, DetailedStatisticsCategory>();
  const problemMap = new Map<number, (typeof problems)[number]>();
  for (const problem of problems) {
    const problemId = problem.problemId ?? problem.id;
    if (typeof problemId === "number") {
      problemMap.set(problemId, problem);
    }
  }

  const getProblemName = (problemId: number): string | null => {
    if (!Array.isArray(problems)) return null;
    const p = problemMap.get(problemId);
    return p?.name ?? p?.title ?? null;
  };

  const getProblemRating = (problemId: number): number => {
    const problem = problemMap.get(problemId);
    const rating =
      typeof problem?.rating === "number"
        ? problem.rating
        : typeof attempts.find((attempt) => attempt?.problemId === problemId)
              ?.taskRating === "number"
          ? attempts.find((attempt) => attempt?.problemId === problemId)!
              .taskRating
          : 1000;
    return rating;
  };

  const isWin = (res: SessionTaskAttemptResultEnum | string | undefined) =>
    res === SessionTaskAttemptResultEnum.WIN || res === "WIN" || res === "1-0";
  const isDraw = (res: SessionTaskAttemptResultEnum | string | undefined) =>
    res === SessionTaskAttemptResultEnum.DRAW ||
    res === "DRAW" ||
    res === "1/2-1/2";
  const isLoss = (res: SessionTaskAttemptResultEnum | string | undefined) =>
    res === SessionTaskAttemptResultEnum.LOSS ||
    res === "LOSS" ||
    res === "0-1";

  const completedProblemIds = new Set<number>();
  let earnedPointsAll = 0;
  let maxPointsAll = 0;
  let earnedPointsSolved = 0;
  let maxPointsSolved = 0;
  let solvedCount = 0;

  // Consider main attempts only: map of latest main attempt per problemId
  const mainAttempts = new Map<number, SessionTaskAttempt>();
  for (const a of attempts || []) {
    if (a == null) continue;
    const isMain =
      a.mainAttemptId === null ||
      a.mainAttemptId === undefined ||
      a.mainAttemptId === 0;
    if (!isMain) continue;
    // Only consider attempts with a final result (win/draw/loss)
    if (
      !(
        a.finalExternalHint ||
        isWin(a.finalResult) ||
        isDraw(a.finalResult) ||
        isLoss(a.finalResult)
      )
    )
      continue;
    // keep last main attempt per problem
    mainAttempts.set(a.problemId, a);
  }

  for (const attempt of mainAttempts.values()) {
    const retryCount =
      typeof attempt.retryCountUsed === "number" ? attempt.retryCountUsed : 0;
    const earnedPoints =
      typeof attempt.pointsChange === "number" ? attempt.pointsChange : 0;
    const maxPoints =
      lifeRating > 0
        ? calculateRatingDeltaFromLife(
            lifeRating,
            getProblemRating(attempt.problemId),
            SessionTaskAttemptResultEnum.WIN,
            0,
            40,
          )
        : 0;

    let type = StatsCategoryType.WIN;

    if (attempt.finalExternalHint) {
      type = StatsCategoryType.EXTERNAL_HINT;
    } else if (isLoss(attempt.finalResult)) {
      type = StatsCategoryType.LOSS;
    } else if (isDraw(attempt.finalResult)) {
      type = StatsCategoryType.DRAW;
    } else {
      type = StatsCategoryType.WIN;
    }

    const key = `${type}-${retryCount}`;

    let cat = map.get(key);
    if (!cat) {
      cat = {
        type,
        retryCount,
        count: 0,
        earnedPoints: 0,
        maxPoints: 0,
        tasks: [],
      };
      map.set(key, cat);
    }

    cat.count += 1;
    cat.earnedPoints += earnedPoints;
    cat.maxPoints += maxPoints;
    earnedPointsAll += earnedPoints;
    maxPointsAll += maxPoints;

    const name = getProblemName(attempt.problemId) ?? null;
    if (name !== null) {
      cat.tasks.push({ problemId: attempt.problemId, name });
    }
    if (typeof attempt.problemId === "number")
      completedProblemIds.add(attempt.problemId);

    if (
      type === StatsCategoryType.WIN ||
      type === StatsCategoryType.DRAW ||
      type === StatsCategoryType.EXTERNAL_HINT
    ) {
      solvedCount += 1;
      earnedPointsSolved += earnedPoints;
      maxPointsSolved += maxPoints;
    }
  }

  if (lifeRating > 0) {
    for (const problem of problems) {
      const problemId = problem.problemId ?? problem.id;
      if (typeof problemId !== "number") continue;
      if (completedProblemIds.has(problemId)) continue;
      maxPointsAll += calculateRatingDeltaFromLife(
        lifeRating,
        getProblemRating(problemId),
        SessionTaskAttemptResultEnum.WIN,
        0,
        40,
      );
    }
  }

  const categories = Array.from(map.values()).filter((c) => c.count > 0);

  // Sort: by retryCount asc, then by type order WIN, DRAW, EXTERNAL_HINT, LOSS
  const typeOrder: Record<StatsCategoryType, number> = {
    [StatsCategoryType.WIN]: 0,
    [StatsCategoryType.DRAW]: 1,
    [StatsCategoryType.EXTERNAL_HINT]: 2,
    [StatsCategoryType.RETRY_GROUP]: 3,
    [StatsCategoryType.LOSS]: 4,
  } as const;

  categories.sort((a, b) => {
    if (a.retryCount !== b.retryCount) return a.retryCount - b.retryCount;
    return (typeOrder[a.type] ?? 0) - (typeOrder[b.type] ?? 0);
  });

  // Group all retry categories (retryCount > 0) into a single RETRY_GROUP
  const directCategories = categories.filter((c) => c.retryCount === 0);
  const retryCategories = categories.filter((c) => c.retryCount > 0);

  let finalCategories: DetailedStatisticsCategory[] = directCategories;

  if (retryCategories.length > 0) {
    // sort retryCategories by retryCount ascending
    retryCategories.sort((a, b) => a.retryCount - b.retryCount);

    const groupCount = retryCategories.reduce((s, c) => s + c.count, 0);
    const groupTasks = retryCategories.reduce<DetailedStatisticsCategoryTask[]>(
      (acc, c) => acc.concat(c.tasks),
      [],
    );

    const groupCategory: DetailedStatisticsCategory = {
      type: StatsCategoryType.RETRY_GROUP,
      retryCount: 0,
      count: groupCount,
      earnedPoints: retryCategories.reduce((sum, category) => {
        return sum + category.earnedPoints;
      }, 0),
      maxPoints: retryCategories.reduce((sum, category) => {
        return sum + category.maxPoints;
      }, 0),
      tasks: groupTasks,
      subCategories: retryCategories,
    };

    finalCategories = finalCategories.concat([groupCategory]);
  }

  const completedProblemsCount = completedProblemIds.size;
  const uncompletedCount = Math.max(0, totalCount - completedProblemsCount);

  return {
    ...createEmptyDetailedStatistics(),
    totalCount,
    uncompletedCount,
    solvedCount,
    earnedPointsAll,
    maxPointsAll,
    earnedPointsSolved,
    maxPointsSolved,
    categories: finalCategories,
  };
}

/**
 * Convert ProblemItem-like objects to SessionTaskAttempt array.
 * We declare a minimal inline type to avoid coupling to external ProblemItem definitions.
 */
export function convertProblemItemsToAttempts(
  problemItems: Array<{
    problemId: number;
    problemOrder: number;
    name?: string | null;
    result: SessionTaskAttemptResultEnum;
    state: SessionTaskAttemptStateEnum;
    hintUsed: boolean;
    finalExternalHint?: boolean;
    retryNumber: number;
  }> = [],
): SessionTaskAttempt[] {
  if (!Array.isArray(problemItems)) return [];

  return problemItems.map((pi) => {
    const attemptId = pi.problemId;
    return {
      attemptId,
      sessionId: 0,
      problemId: pi.problemId,
      mainAttemptId: null,
      state: pi.state,
      result: pi.result,
      finalState: pi.state,
      finalResult: pi.result,
      hintUsed: Boolean(pi.hintUsed),
      externalHint: false,
      internalHint: false,
      finalExternalHint: Boolean(pi.finalExternalHint),
      finalInternalHint: false,
      optionalHintUsed: null,
      retryCountUsed: typeof pi.retryNumber === "number" ? pi.retryNumber : 0,
      durationSec: 0,
      score: 0,
      maxScore: 0,
      userMoves: null,
      pointsChange: 0,
      taskRating: 0,
      strictMoves: null,
      questionResult: null,
      lockedMoves: null,
      solutionMoves: null,
      premoves: 0,
      premovesSAN: null,
      revealed: false,
      equivalentMove: null,
      skippedEquivalent: false,
      solveOrder: pi.problemOrder,
      startedAt: null,
      lastUpdatedAt: null,
    } as SessionTaskAttempt;
  });
}

// Helper to format percentages according to requirements:
// - Return "0" when total is 0
// - Round to 1 decimal place
// - Use comma as decimal separator
// - Omit decimal when it's zero (e.g., 50 instead of 50,0)
export function formatPercentage(count: number, total: number): string {
  if (!total) return "0";
  const val = (count / total) * 100;
  const rounded = Math.round(val * 10) / 10;
  const str = rounded.toString();
  return str.replace(".", ",");
}
